import { useCallback, useEffect, useReducer, useState } from "react";
import Input from "../../../../Input/index";
import { FormikHelpers, useFormik } from "formik";
import validationSchema, {
  homeVisitInitialValues,
} from "../../../../../schemas/homeVisit.schema";
import AsyncMultiSelectOne from "../../../../Input/AsyncMultiSelectOne";
import {
  createIntReducer,
  createIntState,
} from "../../../../../reducersFunc/createInteraction";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import { useDisclosure } from "@chakra-ui/react";
import StatusModal from "../../../../StatusModal";
import {
  useFindAllActivitiesLazyQuery,
  useFindAllUsersLazyQuery,
  useRecordHomeVisitMutation,
} from "../../../../../generated/graphql";
import { recordActivityActions } from "../../../../../utils/constants";
import { debounce } from "lodash";

interface homeVisitInput {
  patient: number;
  activities: number[];
  when: string;
  time: string;
  additionalNotes: string;
}

export default function HomeVisit() {
  const [state, dispatch] = useReducer(createIntReducer, createIntState);

  const loggedInUser = useSelector(
    (state: RootState) => state.loggedInUser.data
  );
  const loggedInRole = useSelector(
    (state: RootState) => state.loggedInUser.role
  );
  const loggedInClinic = useSelector(
    (state: RootState) => state.loggedInUser.clinic
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    findAllActivities,
    { data: ActivitiesData, loading: ActivitiesLoading },
  ] = useFindAllActivitiesLazyQuery();

  const [findAllUsers, { data: PatientData, loading: PatientLoading }] =
    useFindAllUsersLazyQuery();

  const [create, { error, loading }] = useRecordHomeVisitMutation();

  const [err, setErr] = useState(false);

  // NOTE: functions ---------------------------------------------

  // --------- PATIENT ---------------------
  function handlePatients(selectedList) {
    formik.setFieldValue("patient", selectedList[0]?.id ?? -1);
  }
  const searchPatient = debounce(async (query) => {
    try {
      await findAllUsers({
        variables: {
          input: {
            search: query,
          },
        },
      });
    } catch (error) {
      dispatch({
        type: recordActivityActions.addPatientsList,
        payload: [{ name: "Error Occurred" }],
      });
    }
  }, 1000);
  const handlePatientsSearchDebounce = useCallback((query) => {
    searchPatient(query);
  }, []);
  // --------- END PATIENT ---------------------

  // --------- ACTIVITIES ---------------------
  function handleActivites(selectedList) {
    const activities = [];
    selectedList.map((x) => activities.push(x.id));
    formik.setFieldValue("activities", activities);
  }

  const searchActivity = debounce(async (query) => {
    try {
      await findAllActivities({
        variables: {
          input: {
            search: query,
          },
        },
      });
    } catch (error) {
      dispatch({
        type: recordActivityActions.addActivitiesList,
        payload: [{ name: "Error Occurred" }],
      });
    }
  }, 1500);

  const handleActivitiesSearchDebounce = useCallback((query) => {
    searchActivity(query);
  }, []);
  // --------- END ACTIVITIES ---------------------

  // NOTE: functions end---------------------------------------------

  async function handleSubmit(
    values: homeVisitInput,
    _formikHelpers: FormikHelpers<homeVisitInput>
  ) {
    setErr(false);
    try {
      await create({
        variables: {
          input: {
            activityId: values.activities,
            chwId: loggedInUser?.id,
            clinicId: loggedInClinic?.id,
            patientId: values.patient,
            role: loggedInRole,
            time: values.time,
            when: values.when,
            additionalNotes: values.additionalNotes,
          },
        },
      });
    } catch (error) {
      setErr(true);
    } finally {
      onOpen();
    }
  }

  const formik = useFormik({
    initialValues: homeVisitInitialValues,
    onSubmit: handleSubmit,
    validationSchema,
  });

  useEffect(() => {
    const newActivities = [];
    if (
      ActivitiesData?.findAllActivities?.data &&
      ActivitiesData?.findAllActivities?.data?.length > 0
    ) {
      ActivitiesData.findAllActivities.data.map((x) =>
        newActivities.push({
          id: x.id,
          name: `${x.title} (A ${x.reward.toFixed(2)})`,
        })
      );
      dispatch({
        type: recordActivityActions.addActivitiesList,
        payload: newActivities,
      });
    } else {
      dispatch({
        type: recordActivityActions.addActivitiesList,
        payload: [{ name: "No Item Found" }],
      });
    }
  }, [ActivitiesData?.findAllActivities?.data]);

  useEffect(() => {
    const newUser = [];
    if (
      PatientData?.findAllUsers?.data &&
      PatientData?.findAllUsers?.data?.length > 0
    ) {
      PatientData.findAllUsers.data.map((x) =>
        newUser.push({
          id: x.id,
          name: `${x.profile.firstName} ${x.profile.lastName} (${x.phoneNumber})`,
        })
      );
      dispatch({
        type: recordActivityActions.addPatientsList,
        payload: newUser,
      });
    } else {
      dispatch({
        type: recordActivityActions.addPatientsList,
        payload: [{ name: "No Item Found" }],
      });
    }
  }, [PatientData?.findAllUsers?.data]);

  return (
    <>
      <h1 className="mt-4">Record Home Visit</h1>
      <form onSubmit={formik.handleSubmit} className="mt-5 flex flex-col gap-4">
        <AsyncMultiSelectOne
          list={state.patientsList}
          addOrRemoveItem={handlePatients}
          handleSearch={handlePatientsSearchDebounce}
          loading={PatientLoading}
          error={formik.touched.patient && formik.errors.patient}
          placeholder={"Patient"}
          displayValue={"name"}
          emptyRecordMsg={"No Patient"}
          id={"patient"}
          selectionLimit={1}
        />

        <AsyncMultiSelectOne
          list={state.activitiesList}
          addOrRemoveItem={handleActivites}
          handleSearch={handleActivitiesSearchDebounce}
          loading={ActivitiesLoading}
          error={formik.touched.activities && formik.errors.activities}
          placeholder={"Activities"}
          displayValue={"name"}
          emptyRecordMsg={"No Activities"}
          id={"activities"}
        />

        <div className="grid grid-cols-2 gap-x-4">
          <Input
            hasError={Boolean(formik.touched?.when && formik.errors?.when)}
            message={formik.errors?.when}
            value={formik.values?.when}
            onChange={formik.handleChange}
            label="When"
            id="when"
            name="when"
            type="date"
          />

          <Input
            hasError={Boolean(formik.touched?.time && formik.errors?.time)}
            message={formik.errors?.time}
            value={formik.values?.time}
            onChange={formik.handleChange}
            label="Time"
            id="time"
            name="time"
            type="time"
          />
        </div>

        <Input
          hasError={Boolean(
            formik.touched?.additionalNotes && formik.errors?.additionalNotes
          )}
          message={formik.errors?.additionalNotes}
          value={formik.values?.additionalNotes}
          onChange={formik.handleChange}
          type="text"
          label="Additional Notes"
          id="additionalNotes"
          name="additionalNotes"
        />

        <button
          type="submit"
          disabled={loading}
          className="bg-[#4285F4] disabled:bg-[#4286f477] disabled:cursor-not-allowed  text-base text-white font-bold px-6 py-2.5 w-[150px] rounded-full self-end"
        >
          {loading ? "Submitting" : "Submit"}
        </button>
      </form>

      {err ? (
        <StatusModal
          isOpen={isOpen}
          onClose={onClose}
          text={error?.message}
          status="Failed"
          samePageNav={true}
          navigateTo={undefined}
        />
      ) : (
        <StatusModal
          isOpen={isOpen}
          onClose={onClose}
          text="Home Visit Recorded. Go to activities to see your full interactions."
          navigateTo="/app/chw/home"
          status={undefined}
          samePageNav={undefined}
        />
      )}
    </>
  );
}
